import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useDirectory from "../hooks/useDirectory";
import useTaskActions from "../hooks/useTaskActions";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { assignableMembers, taskPerformerOptions, workspaceLink } from "../lib/pilots";
import usePilot from "../hooks/usePilot";
import usePilotTeamTasks from "../hooks/usePilotTeamTasks";
import PilotPanel from "../components/PilotPanel";
import CompleteTaskDialog from "../components/CompleteTaskDialog";
import { TaskDetailSheet, DeleteTaskDialog } from "../components/TaskDetailSheet";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowLeft, ArrowUpRight, ShieldAlert } from "lucide-react";
import RelatedMissions from "../components/missions/RelatedMissions";

// The pilot's own working page (/pilotes/:prospectId). It reads ONLY the pilot (and the caller's tasks of that pharmacy):
// it never fetches the prospect, so a `limited` responsible (who cannot open the prospect) reaches it all the same.
export default function PilotWorkspace() {
  const { prospectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pilotState, loadPilot, setPilotData } = usePilot(prospectId);
  const [viewingTask, setViewingTask] = useState(null);

  const canViewTasks = hasPermission(user, "tasks:view");
  const canEditTasks = hasPermission(user, "tasks:edit");
  const canDeleteTasks = hasPermission(user, "tasks:delete");
  const needsDirectory = hasPermission(user, "prospects:assign") || hasPermission(user, "tasks:scope_all") || canEditTasks;

  // The pilots-domain tasks of the whole team (a dedicated read: GET /tasks stays personal). Without `tasks:view` the card simply does not exist.
  const { teamTasks, reloadTeamTasks } = usePilotTeamTasks(prospectId, canViewTasks);
  const tasks = teamTasks.items;
  const directory = useDirectory(needsDirectory);

  const pilot = pilotState.status === "ready" ? pilotState.data : null;
  const access = pilot?.access;
  const mayEditViewing = canEditTasks && !!viewingTask?.can_edit;  // the API accepts changes only from the owner (or a tasks:scope_all holder)
  const prospect = pilot?.prospect || { id: prospectId, pharmacy_name: "" };

  // Names come from the team card and from the task's own assignees (a former member may still hold a task); the directory is only a fallback.
  const resolveAssignee = (id) => {
    if (id === user?.id) return { name: user.name, avatar_url: user.avatar_url };
    const found = (viewingTask?.assignees || []).find((a) => a.id === id) || (pilot?.members || []).find((m) => m.id === id) || directory.find((u) => u.id === id);
    return found ? { name: found.name, avatar_url: found.avatar_url } : null;
  };
  const directoryNames = (ids) => {
    const names = (ids || []).map((id) => resolveAssignee(id)?.name).filter(Boolean);
    return names.length ? names.join(", ") : null;
  };

  const afterTaskChange = () => { reloadTeamTasks({ silent: true }); loadPilot({ silent: true }); };
  const updateTask = (updated) => setViewingTask((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  const {
    completing, setCompleting, deletingTask, setDeletingTask, cycleStatus: cycleTaskStatus, confirmComplete, addAssignee, confirmDelete,
  } = useTaskActions({
    onUpdated: (updated) => { updateTask(updated); afterTaskChange(); },
    onDeleted: (task) => { setViewingTask((prev) => (prev && prev.id === task.id ? null : prev)); afterTaskChange(); },
  });

  if (pilotState.status === "notfound" || pilotState.status === "forbidden") {
    return (
      <div className="max-w-xl mx-auto space-y-4" data-testid="pilot-workspace-unavailable">
        <BackLink />
        <Card className="p-8 text-center space-y-2">
          <ShieldAlert className="mx-auto text-slate-300" size={26} />
          <h2 className="font-medium text-slate-900">Pilote introuvable ou inaccessible</h2>
          <p className="text-sm text-slate-500">Ce pilote n'existe pas, ou vous n'y avez pas accès.</p>
          <Button variant="outline" onClick={() => navigate("/pilotes")}>Retour à la liste</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pilot-workspace" data-access={access || ""}>
      <BackLink />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 truncate" data-testid="workspace-title">{pilot ? prospect.pharmacy_name : "Pilote"}</h2>
          {pilot && <p className="text-sm text-slate-500 truncate">{[prospect.commune, prospect.region].filter(Boolean).join(" · ")}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(access === "full" || pilotState.status === "none") && hasPermission(user, "prospects:view") && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/prospects/${prospectId}`)} data-testid="workspace-open-prospect">
              Fiche prospect <ArrowUpRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
      </div>

      <PilotPanel
        prospect={prospect} pilotState={pilotState} user={user} directory={directory} tasks={tasks} teamTasks={canViewTasks ? teamTasks : null}
        onChange={setPilotData} onReload={() => loadPilot()}
        onTasksChanged={afterTaskChange}
        onOpenTask={(taskId) => { const t = tasks.find((x) => x.id === taskId); if (t) setViewingTask(t); }}
      />

      {pilot && <RelatedMissions prospectId={prospectId} pilotId={pilot.id} testid="pilot-missions" />}

      <TaskDetailSheet
        task={viewingTask} onOpenChange={(open) => !open && setViewingTask(null)}
        canEdit={mayEditViewing} canDelete={canDeleteTasks && !!viewingTask?.can_edit}
        directory={mayEditViewing ? assignableMembers(pilot || {}).map((m) => ({ id: m.id, name: m.name, avatar_url: m.avatar_url })) : []} directoryNames={directoryNames}
        resolveAssignee={resolveAssignee} currentUser={user}
        navigate={(to) => navigate(workspaceLink(to, access, prospectId))}
        onDelete={setDeletingTask} onMarkDone={(t) => setCompleting({ task: t })}
        onPostpone={(t, dueDate) => patchTask(t, { due_date: dueDate || null }, "Échéance mise à jour", "Impossible de reporter la tâche.")}
        onAddAssignee={addAssignee} onCycleStatus={cycleTaskStatus}
      />
      <CompleteTaskDialog task={completing?.task} user={user} performers={taskPerformerOptions(completing?.task, pilot, prospectId)}
        onClose={() => setCompleting(null)} onConfirm={(_, comment, performer) => confirmComplete(comment, performer)} />
      <DeleteTaskDialog task={deletingTask} onOpenChange={(open) => !open && setDeletingTask(null)} onConfirm={confirmDelete} />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/pilotes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="workspace-back">
      <ArrowLeft size={14} /> Pilotes
    </Link>
  );
}
